import {useAuthStateManager} from '../../hooks/useAuthStateManager.tsx';
import {useEffect, useState} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {
    Collapse,
    Nav,
    Navbar,
    NavbarBrand,
    NavbarToggler,
    NavItem,
    NavLink
} from 'reactstrap';
import Certavue from '../utility/Certavue.tsx';
import {Menu, Settings} from 'lucide-react';
import RoleNames from '../../lib/RoleNames.ts';
import UserMenu from './UserMenu.tsx';
import CompanySwitcher from './CompanySwitcher.tsx';
import NotificationBell from '../notifications/NotificationBell.tsx';
import WhatIfPicker from '../scenarios/WhatIfPicker.tsx';
import {RoutePaths} from '../../RoutePaths';
import GettingStartedNavPill from '../main/getting-started/GettingStartedNavPill';

// The public marketing pages. Each one also renders MarketingSubNav, but that only helps once you
// are already on one of them — so a signed-out visitor needs them in the top menu to get in.
const marketingLinks = [
    {to: RoutePaths.features(), label: 'Features'},
    {to: RoutePaths.productTour(), label: 'Product Tour'},
    {to: RoutePaths.whyCertavue(), label: 'Why Certavue'},
    {to: RoutePaths.aboutUs(), label: 'About Us'},
    {to: RoutePaths.pricing(), label: 'Pricing'}
];

const AppNavbar = () => {
    const {isAuthenticated, isInRole} = useAuthStateManager();
    const canManageDirectory = RoleNames.DirectoryEditors.some(isInRole);
    const [isOpen, setIsOpen] = useState(false);
    const location = useLocation();

    const [currentPath, setCurrentPath] = useState(location.pathname);

    useEffect(() => {
        setCurrentPath(location.pathname);
    }, [location]);

    const toggle = () => setIsOpen(!isOpen);

    const isActive = (path: string) => {
        if (currentPath === path) {
            return true;
        }

        return path !== RoutePaths.home() && currentPath.startsWith(path + '/');
    };

    return (
        <Navbar expand="md" className="cv-navbar navbar-dark">
            <NavbarBrand tag={Link} to={RoutePaths.home()}>
                <Certavue fontSize="2.2rem" style={{marginLeft: '1rem'}} />
            </NavbarBrand>
            <NavbarToggler onClick={toggle}>
                <Menu size={24} />
            </NavbarToggler>
            <Collapse isOpen={isOpen} navbar>
                {/* Left side nav items */}
                <Nav className="me-auto" navbar>
                    {isInRole(RoleNames.CustomerUser) && (
                        <>
                            <NavItem active={isActive(RoutePaths.dashboard())}>
                                <NavLink
                                    tag={Link}
                                    to={RoutePaths.dashboard()}
                                    className={
                                        isActive(RoutePaths.dashboard())
                                            ? 'fw-bold'
                                            : ''
                                    }
                                >
                                    Dashboard
                                </NavLink>
                            </NavItem>
                            <NavItem
                                active={isActive(RoutePaths.insights.root())}
                            >
                                <NavLink
                                    tag={Link}
                                    to={RoutePaths.insights.root()}
                                    className={
                                        isActive(RoutePaths.insights.root())
                                            ? 'fw-bold'
                                            : ''
                                    }
                                >
                                    Insights
                                </NavLink>
                            </NavItem>
                            <NavItem
                                active={isActive(RoutePaths.projects.root())}
                            >
                                <NavLink
                                    tag={Link}
                                    to={RoutePaths.projects.root()}
                                    className={
                                        isActive(RoutePaths.projects.root())
                                            ? 'fw-bold'
                                            : ''
                                    }
                                >
                                    Projects
                                </NavLink>
                            </NavItem>

                            <NavItem active={isActive(RoutePaths.teams.root())}>
                                <NavLink
                                    tag={Link}
                                    to={RoutePaths.teams.root()}
                                    className={
                                        isActive(RoutePaths.teams.root())
                                            ? 'fw-bold'
                                            : ''
                                    }
                                >
                                    Teams
                                </NavLink>
                            </NavItem>
                            {/* Roles, Tags and Working Hours moved into Settings
                                (Directory and Capacity rules). People stays: it is
                                daily operational work, not configuration.
                                Maintainers manage people too. */}
                            {canManageDirectory && (
                                <NavItem
                                    active={isActive(RoutePaths.people.root())}
                                >
                                    <NavLink
                                        tag={Link}
                                        to={RoutePaths.people.root()}
                                        className={
                                            isActive(RoutePaths.people.root())
                                                ? 'fw-bold'
                                                : ''
                                        }
                                    >
                                        People
                                    </NavLink>
                                </NavItem>
                            )}
                        </>
                    )}
                    {!isAuthenticated &&
                        marketingLinks.map(link => (
                            <NavItem key={link.to} active={isActive(link.to)}>
                                <NavLink
                                    tag={Link}
                                    to={link.to}
                                    className={
                                        isActive(link.to) ? 'fw-bold' : ''
                                    }
                                >
                                    {link.label}
                                </NavLink>
                            </NavItem>
                        ))}
                    <NavItem active={isActive(RoutePaths.documents.root())}>
                        <NavLink
                            tag={Link}
                            to={RoutePaths.documents.root()}
                            className={
                                isActive(RoutePaths.documents.root())
                                    ? 'fw-bold'
                                    : ''
                            }
                        >
                            Documentation
                        </NavLink>
                    </NavItem>
                    {!isAuthenticated && (
                        <>
                            <NavItem
                                active={isActive(RoutePaths.accounts.login())}
                            >
                                <NavLink
                                    tag={Link}
                                    to={RoutePaths.accounts.login()}
                                    className={
                                        isActive(RoutePaths.accounts.login())
                                            ? 'fw-bold'
                                            : ''
                                    }
                                >
                                    <b>Login</b>
                                </NavLink>
                            </NavItem>
                            <NavItem
                                active={isActive(RoutePaths.accounts.signUp())}
                            >
                                <NavLink
                                    tag={Link}
                                    to={RoutePaths.accounts.signUp()}
                                    className={
                                        isActive(RoutePaths.accounts.signUp())
                                            ? 'fw-bold'
                                            : ''
                                    }
                                >
                                    <b>Sign up</b>
                                </NavLink>
                            </NavItem>
                        </>
                    )}
                </Nav>
                {/* Right side: What-if lens, Notifications and UserMenu */}
                <Nav className="ms-auto" navbar>
                    {isInRole(RoleNames.CustomerUser) && (
                        <GettingStartedNavPill
                            active={isActive(RoutePaths.gettingStarted())}
                        />
                    )}
                    {isAuthenticated && <WhatIfPicker />}
                    {isAuthenticated && <CompanySwitcher />}
                    {isAuthenticated && <NotificationBell />}
                    {canManageDirectory && (
                        <NavItem active={isActive(RoutePaths.settings.root())}>
                            <NavLink
                                tag={Link}
                                to={RoutePaths.settings.root()}
                                className="d-flex align-items-center"
                                title="Settings"
                                aria-label="Settings"
                            >
                                <Settings size={18} />
                                <span className="d-md-none ms-2">Settings</span>
                            </NavLink>
                        </NavItem>
                    )}
                    <UserMenu />
                </Nav>
            </Collapse>
        </Navbar>
    );
};

export default AppNavbar;
